'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import { useSpring, animated } from 'react-spring';

import { THUMB_FOCUS_GRADIENT } from '@/constants';
import useSound from '@/hooks/use-sound';

interface Props extends React.HTMLAttributes<HTMLInputElement> {
  id?: string;
  name: string;
  size?: number;
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  wrapperStyle?: React.CSSProperties;
  children?: React.ReactNode;
}

function RadioButton({
  id: providedId,
  size = 20,
  checked,
  onChange,
  children,
  wrapperStyle = {},
  ...delegated
}: Props) {
  const generatedId = React.useId();
  const id = providedId || generatedId;

  const [active, setActive] = React.useState(false);

  const [playActive] = useSound('/sounds/pop-down.mp3', {
    volume: 0.25,
  });
  const [playOn] = useSound('/sounds/pop-up-on.mp3', {
    volume: 0.25,
  });
  const [playOff] = useSound('/sounds/pop-up-off.mp3', {
    volume: 0.25,
  });

  // The outline shrinks while the mouse is pressed. We want to shrink it by 2px, regardless of `size`.
  const shrinkRatio = size / (size + 2);
  const outlineSpring = useSpring({
    transform: `scale(${active ? shrinkRatio : 1})`,
    config: {
      tension: 400,
      friction: 10,
      clamp: active,
    },
  });

  const fillSpring = useSpring({
    transform: `scale(${checked ? 1 : 0})`,
    config: {
      tension: 400,
      friction: checked ? 20 : 60,
      clamp: !checked,
    },
  });

  return (
    <Wrapper
      htmlFor={id}
      style={wrapperStyle}
      onMouseDown={() => {
        setActive(true);
        playActive();
      }}
      onMouseUp={() => {
        setActive(false);

        if (checked) {
          playOff();
        } else {
          playOn();
        }
      }}
      onMouseLeave={() => {
        setActive(false);
      }}
    >
      <RadioWrapper style={{ width: size, height: size }}>
        <Outline style={outlineSpring} />
        <Fill
          style={{
            ...fillSpring,
            opacity: checked ? 1 : 0,
            '--opacity-transition-duration': checked
              ? '250ms'
              : '500ms',
          }}
        />
        <RealInput
          type="radio"
          id={id}
          checked={checked}
          onChange={onChange}
          {...delegated}
        />
      </RadioWrapper>

      {children}
    </Wrapper>
  );
}

const Wrapper = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
`;

const RealInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  appearance: none;
  cursor: pointer;
`;

const RadioWrapper = styled.div`
  position: relative;
  padding: 4px;
`;

const Outline = styled(animated.div)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: 1000px;
  border: 2px solid var(--color-gray-700);
  will-change: transform;
`;

const Fill = styled(animated.div)`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 1000px;
  background: hsl(50deg 100% 50%);
  background: oklch(0.9 0.21 97.45);
  transition: opacity var(--opacity-transition-duration);
  will-change: transform;
`;

export default RadioButton;
